/**
 * Projection de la casse et de la rupture d'un appel de réappro (onglet Pilotage › Réappro).
 *
 * Calcul pur, partagé par le backend (agrégats, instantanés du recalage) et le back-office (recalcul
 * en direct quand on bouge un coef). Parti de la maquette v7 (`02-simulateur.html`, `unit0`,
 * `run`, `evalRef`, `evalCat`, `viser`, `volCat`), dont il s'écarte sur la vente et le stock :
 *
 * - une **référence × boutique** à la fois, lue sur 3 jours au plus depuis la livraison de l'appel,
 *   le lot de l'appel étant suivi jusqu'à sa fin de vie ;
 * - la demande d'un jour vaut prévision + z × écart-type, avec **le même z tous les jours**
 *   (erreurs corrélées) sur une grille de 61 quantiles de la loi normale ;
 * - le stock est suivi **par lot de DLC** : les ventes prennent la DLC la plus courte d'abord, comme
 *   la demande prévue d'ici la livraison, servie jour par jour sur ce qui est encore vendable ce
 *   jour-là ; un lot part en casse le soir de sa DLC, la demande non servie est perdue ;
 * - les commandes sont fixées une fois, sur la demande prévue (z = 0), puis rejouées sur tous les
 *   scénarios : la commande ne connaît pas la demande réelle ;
 * - par scénario, l'issue d'une référence × boutique est **exclusive** : des invendus, ou sinon une
 *   rupture, jamais les deux. Le cumul n'existe qu'entre boutiques ou entre références.
 *
 * La même simulation se lit de trois façons, à ne pas mélanger (mesurées contre le rejeu, du 22/10
 * au 01/11) :
 *
 * - `daily` : le flux d'une date, ce que montre l'écran sur les jours que l'appel couvre. Chaque
 *   référence × boutique × jour n'y compte qu'une fois, comme au rejeu et au S-1. Il réagit peu au
 *   coef : le surplus d'une commande part en casse à sa fin de vie, souvent après ces jours-là.
 * - `horizon` : le cumul sur la vie du lot, 3 jours au plus. Deux appels successifs y comptent les
 *   mêmes jours : c'est un flux à comparer au réel de la même fenêtre (instantanés du recalage). Sa
 *   demande et sa rupture s'arrêtent aux jours qu'une livraison simulée couvre.
 * - `orderThrown` : la casse de la commande, ce qu'il reste du lot de l'appel à sa fin de vie, suivi
 *   au-delà de l'horizon (`PILOTAGE_LOT_DAYS`). Chaque lot n'y compte qu'une fois, et c'est la seule
 *   lecture de la casse qui réponde au coef sur toutes les DLC : coef visé, leviers.
 */
import { computeForecastReapproBesoin } from './reappro.tools.js'
import { normalQuantile, round } from './maths.tools.js'
import { addDaysIso, dayDiffIso } from './reapproProjectionLots.tools.js'

// ─── Scénarios de demande ────────────────────────────────────────────────────

export const PILOTAGE_QUANTILE_COUNT = 61

/** Grille des scénarios de demande : quantiles (i + ½) / 61 de la loi normale. */
export const PILOTAGE_QUANTILES: readonly number[] = Array.from(
  { length: PILOTAGE_QUANTILE_COUNT },
  (_, i) => normalQuantile((i + 0.5) / PILOTAGE_QUANTILE_COUNT),
)

/** z du 90e centile : scénarios « ventes basses » (p10) et « ventes hautes » (p90). */
export const PILOTAGE_Z90 = 1.2816

/** Jours lus depuis la livraison de l'appel : casse et rupture sur la vie du lot, 72 h au plus. */
export const PILOTAGE_HORIZON_DAYS = 3

/**
 * Plafond du suivi du lot de l'appel, qui continue après l'horizon sans nouvelle livraison : la DLC
 * la plus courte partant d'abord, son sort ne dépend pas des livraisons suivantes. Interne au calcul,
 * rien de plus à l'écran.
 */
export const PILOTAGE_LOT_DAYS = 7

/**
 * Jours à simuler pour finir le lot d'une livraison, l'horizon au moins. Un lot qui vit au-delà du
 * plafond n'est pas suivi : sa casse compte 0.
 */
export const pilotageLotDays = (shelfLifeDays: number): number =>
  shelfLifeDays < PILOTAGE_LOT_DAYS
    ? Math.max(PILOTAGE_HORIZON_DAYS, shelfLifeDays + 1)
    : PILOTAGE_HORIZON_DAYS

/** Seuil d'une issue : une unité jetée ou perdue au moins (demande continue). */
const ISSUE_THRESHOLD = 0.5
const EMPTY_LOT = 1e-9

// ─── Entrées ─────────────────────────────────────────────────────────────────

/**
 * Règle de commande d'une livraison, fonction pure du coef.
 *
 * - `fixed` : besoin déjà calculé (ou boutique en calcul traditionnel) ; le coef n'y change rien.
 * - `forecast` : règle du réappro en méthode prévision (`computeForecastReapproBesoin`),
 *   `max(0, arrondi(prévision des jours couverts × coef − stock projeté))`, puis commande arrêtée,
 *   ajustement daté et arrondi au colis. `projectedStock` vient du réappro (stock actuel − DLC
 *   courte + arrivant − conso intermédiaire, à l'heure de calcul) ; absent, la simulation prend le
 *   stock encore valide le jour de la livraison. `maxUnits` : plafond de production atteint au coef
 *   actuel, la commande ne le dépasse pas.
 */
export type PilotageOrderRule =
  | { kind: 'fixed'; units: number }
  | {
      kind: 'forecast'
      colisage: number
      stopOrdering: boolean
      adjustment: number
      projectedStock: number | null
      maxUnits: number | null
    }

export interface PilotageDelivery {
  /** Jour de livraison, compté depuis la livraison de l'appel (0 = l'appel). */
  day: number
  /** Jours couverts, comptés depuis la livraison de l'appel. */
  coveredDays: readonly number[]
  /** Jours de vente du lot après son jour de livraison : casse le soir de `day + shelfLifeDays`. */
  shelfLifeDays: number
  rule: PilotageOrderRule
  /** Coef de sécurité effectif de cette livraison (produit × boutique × date). */
  coef: number
}

export interface PilotageStockLot {
  /** DLC imprimée : dernier jour de vente, casse le soir. */
  dlcIso: string
  units: number
}

export interface PilotageUnitInput {
  /** Livraison de l'appel (jour 0). */
  deliveryIso: string
  /**
   * Prévision par jour depuis le jour 0, jusqu'à la fin de vie du lot de l'appel. Dans l'horizon, un
   * jour absent compte 0 ; après, il n'est pas simulé.
   */
  forecast: readonly number[]
  /** Volatilité d'un jour (écart-type / prévision), même indice que `forecast`. */
  cv: readonly number[]
  /**
   * Cumul mesuré sur N jours, rapporté à un jour : `cumulRatios[k]` = e(k + 1) / e(1), pour un
   * appel de k + 1 jours (k = 0 vaut 1). `null` : repli √(1 + rang du jour dans la livraison).
   */
  cumulRatios: readonly number[] | null
  /**
   * Stock relevé en boutique, par DLC, y compris ce qui périme avant la livraison : ces lots servent
   * la demande de leurs derniers jours de vente.
   */
  stock: readonly PilotageStockLot[]
  /**
   * Demande prévue de chaque jour d'ici la livraison, la veille en dernier : servie jour par jour sur
   * les DLC les plus courtes encore vendables ce jour-là.
   */
  demandBeforeDays: readonly number[]
  /** La livraison de l'appel (jour 0) puis les suivantes de la fenêtre simulée. */
  deliveries: readonly PilotageDelivery[]
  /**
   * Multiplicateur que porte la prévision officielle de chaque jour (0,2 = +20 %), déjà compris dans
   * `forecast`. Absent : aucun.
   */
  multipliers?: readonly number[] | null
  /**
   * Prévision du moteur par jour, avant ce multiplicateur : la base d'un multiplicateur candidat
   * (après un −100 %, la prévision officielle est nulle et ne dit plus rien du moteur). Absente :
   * la prévision officielle, divisée par `multipliers` s'il y en a un.
   */
  engineForecast?: readonly number[] | null
}

/**
 * Leviers candidats de l'opérateur. Absents : l'état actuel (coef effectif de chaque livraison,
 * multiplicateur en vigueur).
 *
 * - `coef` remplace le coef de toutes les livraisons de la fenêtre.
 * - `multiplier` (−1 à +1) remplace le multiplicateur en vigueur sur tous les jours : prévision =
 *   prévision du moteur × (1 + candidat). La demande simulée suit la prévision.
 */
export interface PilotageLevers {
  coef?: number
  multiplier?: number
}

// ─── Sorties ─────────────────────────────────────────────────────────────────

/** Fin de journée d'un scénario, en cumul depuis le jour 0. */
export interface PilotageScenarioDay {
  thrown: number
  /** Part de `thrown` qui vient du lot de l'appel. */
  orderThrown: number
  lost: number
  demand: number
  remaining: number
}

/** Moyenne des 61 scénarios, en cumul jusqu'au jour i. Issues exclusives : pThrown + pLost ≤ 1. */
export interface PilotageHorizonDay {
  thrown: number
  lost: number
  demand: number
  supplied: number
  pThrown: number
  pLost: number
}

/** Un seul jour d'un scénario moyen (pas un cumul). Issues exclusives du jour. */
export interface PilotageDayOutcome {
  dateIso: string
  forecast: number
  thrown: number
  /** Part de `thrown` qui vient du lot de l'appel. */
  orderThrown: number
  lost: number
  demand: number
  pThrown: number
  pLost: number
}

export interface PilotageUnitResult {
  /** Coef de la livraison de l'appel, tel que simulé. */
  coef: number
  /** Multiplicateur du jour de l'appel tel que simulé (0 : aucun). */
  multiplier: number
  /** Dernier jour compté dans la casse et la rupture : vie du lot livré, 3 jours au plus. */
  horizonIndex: number
  /** Prévision des jours couverts par la livraison de l'appel. */
  forecast: number
  /** Écart-type de la demande sur ces jours (cumul de l'appel). */
  sd: number
  /** Stock utilisable à la livraison (DLC ≥ livraison, après la demande d'ici là). */
  usableStock: number
  /** Commande de l'appel. */
  order: number
  /**
   * Casse de la commande : ce qu'il reste du lot de l'appel le soir de sa DLC, moyenne des 61
   * scénarios. Le lot est suivi jusqu'à `PILOTAGE_LOT_DAYS` : elle ne dépend pas de l'horizon.
   */
  orderThrown: number
  /** La commande de l'appel est déjà calculée : le coef n'y change plus rien. */
  orderFixed: boolean
  /**
   * Stock que la règle de commande de l'appel retranche du besoin, min(stock, prévision × coef) :
   * « Le calcul » s'additionne ligne à ligne (`null` : commande figée).
   */
  deductedStock: number | null
  days: { dateIso: string; forecast: number; sd: number }[]
  deliveries: { day: number; units: number; estimated: boolean }[]
  horizon: PilotageHorizonDay[]
  /** Casse et rupture de chaque jour seul, jour 0 = livraison de l'appel. */
  daily: PilotageDayOutcome[]
  scenarios: { p10: PilotageScenarioDay[]; p50: PilotageScenarioDay[]; p90: PilotageScenarioDay[] }
  /** Stock utilisable par DLC. */
  lots: PilotageStockLot[]
}

// ─── Simulation d'une référence × boutique ───────────────────────────────────

/** Ratio de cumul du rang `rank`, le dernier mesuré au-delà. */
const cumulRatioAt = (ratios: readonly number[], rank: number): number =>
  ratios[Math.min(rank, ratios.length - 1)] ?? 1

const at = (values: readonly number[], index: number): number => {
  const value = values[index]
  return value !== undefined && Number.isFinite(value) ? value : 0
}

/**
 * Écart-type de la demande de chaque jour. Hors livraison : cv × prévision. Pour les jours couverts
 * par une livraison, l'incertitude qui compte est celle de la somme : S_k = r(k + 1) × Σ cv × F sur
 * les k + 1 premiers jours couverts, et le jour k porte S_k − S_(k−1). Sans mesure du cumul :
 * cv × F × √(1 + k).
 */
export const dailyDemandSd = (
  forecast: readonly number[],
  cv: readonly number[],
  deliveries: readonly Pick<PilotageDelivery, 'coveredDays'>[],
  cumulRatios: readonly number[] | null,
): number[] => {
  const sd = forecast.map((units, day) => at(cv, day) * units)
  for (const delivery of deliveries) {
    let cumulated = 0
    let previous = 0
    delivery.coveredDays.forEach((day, rank) => {
      if (day >= forecast.length) return
      const spread = at(cv, day) * at(forecast, day)
      if (!cumulRatios || cumulRatios.length === 0) {
        sd[day] = spread * Math.sqrt(1 + rank)
        return
      }
      cumulated += spread
      const ratio = cumulRatioAt(cumulRatios, rank)
      const current = ratio * cumulated
      sd[day] = Math.max(0, current - previous)
      previous = Math.max(previous, current)
    })
  }
  return sd
}

/** Stock retranché du besoin : celui du réappro, sinon le stock simulé, au plus le besoin. */
const deductedStockOf = (
  rule: PilotageOrderRule,
  simulatedStock: number,
  need: number,
): number | null =>
  rule.kind === 'fixed' ? null : Math.min(Math.max(0, rule.projectedStock ?? simulatedStock), need)

/** Commande d'une livraison au coef donné : règle du réappro, pure. */
export const pilotageOrderUnits = (
  rule: PilotageOrderRule,
  coveredDemand: number,
  coef: number,
  simulatedStock: number,
): number => {
  if (rule.kind === 'fixed') return Math.max(0, rule.units)
  const { besoin } = computeForecastReapproBesoin({
    coveredDemand,
    intermediateDemand: 0,
    coefSecu: coef,
    stockActuel: rule.projectedStock ?? simulatedStock,
    stockDLCCourte: 0,
    stockArrivant: 0,
    stopOrdering: rule.stopOrdering,
    colisage: rule.colisage,
    needAdjustmentMultiplier: rule.adjustment,
  })
  return rule.maxUnits === null ? besoin : Math.min(besoin, Math.max(0, rule.maxUnits))
}

/**
 * Lot simulé : `expiresIn` est son dernier jour de vente (la DLC imprimée), en jours depuis la
 * livraison ; il part en casse le soir de ce jour (maquette : « casse le soir de la DLC »). Un lot
 * livré vit `shelfLifeDays` jours après son jour de livraison. Le stock simulé d'une livraison
 * suivante garde donc le lot dont la DLC tombe ce jour-là, comme le stock relevé que lit la
 * projection du réappro (`loadInitialLots`). Les lots que cette projection fait livrer, eux,
 * sortent la veille de leur DLC (`reapproProjection.tools.ts`, `expireLots`) : écart propre à ce
 * moteur. Le soir de la DLC est mesuré : une sortie la veille doublerait la casse contre le rejeu.
 */
interface SimLot {
  expiresIn: number
  units: number
  /** Lot de la commande de l'appel, celui de `orderThrown`. */
  ofCall?: boolean
}

/**
 * Sert `demand` sur des lots, dans leur ordre, et rend ce qui reste non servi. `sellFifo` fait de même
 * en dates ISO, sur des copies : ici, des jours relatifs, sur le chemin chaud des 61 scénarios.
 */
const serveLots = (lots: readonly SimLot[], demand: number): number => {
  let unserved = demand
  for (const lot of lots) {
    if (unserved <= 0) break
    const sold = Math.min(lot.units, unserved)
    lot.units -= sold
    unserved -= sold
  }
  return unserved
}

/**
 * Stock à la livraison : la demande d'ici là prend, jour par jour, la DLC la plus courte encore
 * vendable, y compris les lots qui périment avant la livraison. Le stock ne dit pas quand un lot
 * arrive : une livraison d'ici là compte comme déjà en rayon.
 */
const usableLots = (input: PilotageUnitInput): (SimLot & { dlcIso: string })[] => {
  const lots = input.stock
    .map((lot) => ({
      expiresIn: dayDiffIso(input.deliveryIso, lot.dlcIso),
      units: lot.units,
      dlcIso: lot.dlcIso.slice(0, 10),
    }))
    .sort((a, b) => a.expiresIn - b.expiresIn)
  input.demandBeforeDays.forEach((units, index) => {
    const offset = index - input.demandBeforeDays.length
    serveLots(
      lots.filter((lot) => lot.expiresIn >= offset),
      Math.max(0, units),
    )
  })
  return lots.filter((lot) => lot.expiresIn >= 0 && lot.units > 0)
}

const sumOver = (values: readonly number[], days: readonly number[]) =>
  days.reduce((sum, day) => sum + at(values, day), 0)

/**
 * Prévision du moteur d'un jour : l'officielle, sans le multiplicateur en vigueur. Le serveur
 * l'envoie (`engineForecast`) dès qu'un multiplicateur s'applique, seul moyen de la retrouver
 * sous un −100 %. Sans elle (réponse en cache d'avant ce champ), elle se déduit par division.
 */
export const pilotageEngineForecast = (input: PilotageUnitInput, day: number): number => {
  if (input.engineForecast) return Math.max(0, at(input.engineForecast, day))
  const official = Math.max(0, at(input.forecast, day))
  const current = 1 + at(input.multipliers ?? [], day)
  return current > 0 ? official / current : 0
}

/** Prévision de chaque jour avec le multiplicateur candidat, à la place de celui en vigueur. */
const forecastWith = (input: PilotageUnitInput, day: number, multiplier: number | undefined) =>
  multiplier === undefined
    ? Math.max(0, at(input.forecast, day))
    : pilotageEngineForecast(input, day) * (1 + multiplier)

/**
 * Préparation d'une simulation : livraisons de la fenêtre, prévision et écart-type de chaque jour,
 * stock utilisable, et un scénario (`run`) à z donné, ses commandes calculées (`fixedOrders` null)
 * ou imposées.
 */
const preparePilotageUnit = (input: PilotageUnitInput, levers: PilotageLevers) => {
  const coefOf = (delivery: PilotageDelivery) => levers.coef ?? delivery.coef
  const deliveries = input.deliveries
    .filter((delivery) => delivery.day >= 0 && delivery.day < PILOTAGE_HORIZON_DAYS)
    .sort((a, b) => a.day - b.day)
  const call = deliveries[0]?.day === 0 ? deliveries[0] : undefined
  // Jours du lot de l'appel, ceux pour lesquels l'entrée porte une prévision.
  const lotDays = Math.max(
    PILOTAGE_HORIZON_DAYS,
    Math.min(pilotageLotDays(call?.shelfLifeDays ?? 0), input.forecast.length),
  )
  let length = lotDays
  for (const delivery of deliveries) {
    for (const day of delivery.coveredDays) length = Math.max(length, day + 1)
  }
  const forecast = Array.from({ length }, (_, day) =>
    Math.max(0, forecastWith(input, day, levers.multiplier)),
  )
  const cv = Array.from({ length }, (_, day) => Math.max(0, at(input.cv, day)))
  const sd = dailyDemandSd(forecast, cv, deliveries, input.cumulRatios)
  const initialLots = usableLots(input)
  const usableStock = initialLots.reduce((sum, lot) => sum + lot.units, 0)
  // Un jour qu'aucune livraison simulée ne couvre vend le lot en vie, mais ne compte pas de rupture :
  // le plus souvent, c'est la prévision de sa livraison qu'on ne lit pas encore (dernières vues
  // lisibles), et rien ne dit qu'il manquera quelque chose ce jour-là.
  const covered = new Set(deliveries.flatMap((delivery) => delivery.coveredDays))
  const countsDemand = (day: number) => covered.size === 0 || covered.has(day)

  const run = (z: number, fixedOrders: number[] | null) => {
    let lots: SimLot[] = initialLots.map((lot) => ({ expiresIn: lot.expiresIn, units: lot.units }))
    let thrown = 0
    let lost = 0
    let demand = 0
    let orderThrown = 0
    const orders = fixedOrders ?? []
    const days: PilotageScenarioDay[] = []
    // Le scénario médian fixe les commandes : il s'arrête à l'horizon, son `orderThrown` ne se lit pas.
    const lastDay = fixedOrders ? lotDays : PILOTAGE_HORIZON_DAYS
    for (let day = 0; day < lastDay; day++) {
      // Après l'horizon, seul compte le lot de l'appel.
      if (day >= PILOTAGE_HORIZON_DAYS && !lots.some((lot) => lot.ofCall)) break
      deliveries.forEach((delivery, index) => {
        if (delivery.day !== day) return
        const units = fixedOrders
          ? (fixedOrders[index] ?? 0)
          : pilotageOrderUnits(
              delivery.rule,
              sumOver(forecast, delivery.coveredDays),
              coefOf(delivery),
              lots.reduce((sum, lot) => sum + (lot.expiresIn >= day ? lot.units : 0), 0),
            )
        if (!fixedOrders) orders[index] = units
        if (units > 0) {
          lots.push({ expiresIn: day + delivery.shelfLifeDays, units, ofCall: delivery === call })
        }
      })
      lots.sort((a, b) => a.expiresIn - b.expiresIn)
      const dayDemand = Math.max(0, forecast[day]! + z * sd[day]!)
      const unserved = serveLots(lots, dayDemand)
      if (countsDemand(day)) {
        demand += dayDemand
        lost += unserved
      }
      lots = lots.filter((lot) => {
        if (lot.expiresIn <= day) {
          thrown += lot.units
          if (lot.ofCall) orderThrown += lot.units
          return false
        }
        return lot.units > EMPTY_LOT
      })
      if (day >= PILOTAGE_HORIZON_DAYS) continue
      days.push({
        thrown,
        lost,
        demand,
        orderThrown,
        remaining: lots.reduce((sum, lot) => sum + lot.units, 0),
      })
    }
    return { days, orders, orderThrown }
  }

  return { call, coefOf, deliveries, forecast, sd, initialLots, usableStock, run }
}

/** Cumuls d'avant le jour 0. */
const NO_DAY_YET = { thrown: 0, lost: 0, demand: 0, orderThrown: 0 }

export const simulatePilotageUnit = (
  input: PilotageUnitInput,
  levers: PilotageLevers = {},
): PilotageUnitResult => {
  const { call, coefOf, deliveries, forecast, sd, initialLots, usableStock, run } =
    preparePilotageUnit(input, levers)
  const median = run(0, null)
  const orders = deliveries.map((_, index) => median.orders[index] ?? 0)
  const supplied: number[] = []
  let supply = usableStock
  for (let day = 0; day < PILOTAGE_HORIZON_DAYS; day++) {
    deliveries.forEach((delivery, index) => {
      if (delivery.day === day) supply += orders[index]!
    })
    supplied.push(supply)
  }
  const horizon: PilotageHorizonDay[] = supplied.map((value) => ({
    thrown: 0,
    lost: 0,
    demand: 0,
    supplied: value,
    pThrown: 0,
    pLost: 0,
  }))
  const daily: PilotageDayOutcome[] = Array.from({ length: PILOTAGE_HORIZON_DAYS }, (_, day) => ({
    dateIso: addDaysIso(input.deliveryIso.slice(0, 10), day),
    forecast: forecast[day]!,
    thrown: 0,
    orderThrown: 0,
    lost: 0,
    demand: 0,
    pThrown: 0,
    pLost: 0,
  }))
  const weight = 1 / PILOTAGE_QUANTILE_COUNT
  let orderThrown = 0
  for (const z of PILOTAGE_QUANTILES) {
    const { days: scenario, orderThrown: thrownOfLot } = run(z, orders)
    orderThrown += thrownOfLot * weight
    for (let day = 0; day < PILOTAGE_HORIZON_DAYS; day++) {
      const cell = horizon[day]!
      const outcome = scenario[day]!
      cell.thrown += outcome.thrown * weight
      cell.lost += outcome.lost * weight
      cell.demand += outcome.demand * weight
      if (outcome.thrown > ISSUE_THRESHOLD) cell.pThrown += weight
      else if (outcome.lost > ISSUE_THRESHOLD) cell.pLost += weight
      const previous = day > 0 ? scenario[day - 1]! : NO_DAY_YET
      const dayThrown = outcome.thrown - previous.thrown
      const dayLost = outcome.lost - previous.lost
      const single = daily[day]!
      single.thrown += dayThrown * weight
      single.orderThrown += (outcome.orderThrown - previous.orderThrown) * weight
      single.lost += dayLost * weight
      single.demand += (outcome.demand - previous.demand) * weight
      if (dayThrown > ISSUE_THRESHOLD) single.pThrown += weight
      else if (dayLost > ISSUE_THRESHOLD) single.pLost += weight
    }
  }

  const coveredByCall = call?.coveredDays ?? [0]
  const callForecast = sumOver(forecast, coveredByCall)
  const coef = call ? coefOf(call) : (levers.coef ?? 1)
  return {
    coef,
    multiplier: levers.multiplier ?? at(input.multipliers ?? [], 0),
    horizonIndex: Math.min(PILOTAGE_HORIZON_DAYS, (call?.shelfLifeDays ?? 0) + 1) - 1,
    forecast: callForecast,
    sd: sumOver(sd, coveredByCall),
    usableStock,
    order: call ? orders[0]! : 0,
    orderThrown,
    orderFixed: call?.rule.kind === 'fixed',
    deductedStock: call ? deductedStockOf(call.rule, usableStock, callForecast * coef) : null,
    days: Array.from({ length: PILOTAGE_HORIZON_DAYS }, (_, day) => ({
      dateIso: addDaysIso(input.deliveryIso.slice(0, 10), day),
      forecast: forecast[day]!,
      sd: sd[day]!,
    })),
    deliveries: deliveries.map((delivery, index) => ({
      day: delivery.day,
      units: orders[index]!,
      estimated: delivery.day > 0,
    })),
    horizon,
    daily,
    scenarios: {
      p10: run(-PILOTAGE_Z90, orders).days,
      p50: median.days,
      p90: run(PILOTAGE_Z90, orders).days,
    },
    lots: initialLots.map((lot) => ({ dlcIso: lot.dlcIso, units: lot.units })),
  }
}

/** Issues exclusives d'une référence × boutique, en jours sur 10 : invendus, puis rupture. */
export const pilotageIssuesOutOfTen = (
  result: Pick<PilotageUnitResult, 'horizon' | 'horizonIndex'>,
): { thrown: number; lost: number } => {
  const cell = result.horizon[result.horizonIndex]
  if (!cell) return { thrown: 0, lost: 0 }
  const thrown = Math.round(cell.pThrown * 10)
  return { thrown, lost: Math.min(10 - thrown, Math.round(cell.pLost * 10)) }
}

// ─── Agrégation (catégorie, réseau) ──────────────────────────────────────────

/** Résumé d'une référence × boutique au coef donné, casse et rupture à son horizon. */
export type PilotageUnitSummary = Pick<
  PilotageUnitResult,
  | 'coef'
  | 'forecast'
  | 'sd'
  | 'usableStock'
  | 'order'
  | 'orderThrown'
  | 'orderFixed'
  | 'deductedStock'
> &
  PilotageHorizonDay

/** Chaque couple compte jusqu'à son propre horizon (vie de son lot). */
export const summarizePilotageUnit = (result: PilotageUnitResult): PilotageUnitSummary => {
  const cell = result.horizon[result.horizonIndex]
  return {
    coef: result.coef,
    forecast: result.forecast,
    sd: result.sd,
    usableStock: result.usableStock,
    order: result.order,
    orderThrown: result.orderThrown,
    orderFixed: result.orderFixed,
    deductedStock: result.deductedStock,
    thrown: cell?.thrown ?? 0,
    lost: cell?.lost ?? 0,
    demand: cell?.demand ?? 0,
    supplied: cell?.supplied ?? 0,
    pThrown: cell?.pThrown ?? 0,
    pLost: cell?.pLost ?? 0,
  }
}

const SUMMED_FIELDS = [
  'forecast',
  'sd',
  'usableStock',
  'order',
  'orderThrown',
  'forecastTimesCoef',
  'openNeed',
  'deductedStock',
  'fixedOrder',
  'thrown',
  'lost',
  'demand',
  'supplied',
  'pThrown',
  'pLost',
] as const

/**
 * Agrégat de couples. « Le calcul » : `openNeed` (prévision × coef des commandes encore à calculer)
 * − `deductedStock` ≈ `order` − `fixedOrder`, aux arrondis, colis et ajustements près.
 */
export interface PilotageAggregate extends Record<(typeof SUMMED_FIELDS)[number], number> {
  /** Couples référence × boutique agrégés. */
  units: number
  /** Coef moyen pondéré par la prévision (Σ prévision × coef / Σ prévision). */
  coef: number
  /** Casse de la commande = `orderThrown` / commande, en %. */
  breakagePct: number
  /** Rupture = perdu / demandé, en %. */
  stockoutPct: number
}

export const emptyPilotageAggregate = (): PilotageAggregate => ({
  units: 0,
  forecast: 0,
  sd: 0,
  usableStock: 0,
  order: 0,
  orderThrown: 0,
  forecastTimesCoef: 0,
  openNeed: 0,
  deductedStock: 0,
  fixedOrder: 0,
  thrown: 0,
  lost: 0,
  demand: 0,
  supplied: 0,
  pThrown: 0,
  pLost: 0,
  coef: 1,
  breakagePct: 0,
  stockoutPct: 0,
})

/** Ratios d'un agrégat : coef moyen pondéré par la prévision, casse et rupture en %. */
const finalizeAggregate = (aggregate: PilotageAggregate, fallbackCoef: number) => {
  aggregate.coef =
    aggregate.forecast > 0 ? aggregate.forecastTimesCoef / aggregate.forecast : fallbackCoef
  aggregate.breakagePct = aggregate.order > 0 ? (aggregate.orderThrown / aggregate.order) * 100 : 0
  aggregate.stockoutPct = aggregate.demand > 0 ? (aggregate.lost / aggregate.demand) * 100 : 0
  return aggregate
}

/** Agrégat de couples (résultats complets ou résumés). */
export const aggregatePilotageUnits = (
  units: readonly (PilotageUnitResult | PilotageUnitSummary)[],
): PilotageAggregate => {
  const total = emptyPilotageAggregate()
  for (const unit of units) {
    const summary = 'horizon' in unit ? summarizePilotageUnit(unit) : unit
    total.units += 1
    total.forecast += summary.forecast
    total.sd += summary.sd
    total.usableStock += summary.usableStock
    total.order += summary.order
    total.orderThrown += summary.orderThrown
    total.forecastTimesCoef += summary.forecast * summary.coef
    total.openNeed += summary.orderFixed ? 0 : summary.forecast * summary.coef
    total.deductedStock += summary.deductedStock ?? 0
    total.fixedOrder += summary.orderFixed ? summary.order : 0
    total.thrown += summary.thrown
    total.lost += summary.lost
    total.demand += summary.demand
    total.supplied += summary.supplied
    total.pThrown += summary.pThrown
    total.pLost += summary.pLost
  }
  return finalizeAggregate(total, units[0]?.coef ?? 1)
}

/**
 * Somme d'agrégats (catégories → total) : les unités s'additionnent, le coef se repondère par la
 * prévision.
 */
export const mergePilotageAggregates = (parts: readonly PilotageAggregate[]): PilotageAggregate => {
  const total = emptyPilotageAggregate()
  for (const part of parts) {
    total.units += part.units
    for (const field of SUMMED_FIELDS) total[field] += part[field]
  }
  return finalizeAggregate(total, parts[0]?.coef ?? 1)
}

// ─── Coef visé ───────────────────────────────────────────────────────────────

export const PILOTAGE_TARGET_COEF_RANGE = { min: 0.5, max: 2.5 } as const

const roundCoef = (coef: number) => round(coef, 2)

/**
 * Coef qui amène la casse projetée à `targetPct` (bissection, 18 pas, arrondi au centième). La
 * casse monte avec le coef. `min` si la casse y dépasse déjà la cible ; `null` si elle reste sous
 * la cible même au coef maximal.
 */
export const viserCasse = (
  evaluate: (coef: number) => Pick<PilotageAggregate, 'breakagePct'> | null,
  targetPct: number,
  range: { min: number; max: number } = PILOTAGE_TARGET_COEF_RANGE,
): number | null => {
  let lo = range.min
  let hi = range.max
  const atMin = evaluate(lo)
  if (!atMin || atMin.breakagePct >= targetPct) return lo
  const atMax = evaluate(hi)
  if (!atMax || atMax.breakagePct < targetPct) return null
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2
    const result = evaluate(mid)
    if (result && result.breakagePct < targetPct) lo = mid
    else hi = mid
  }
  return roundCoef(lo)
}

// ─── Volatilité de l'appel (constat) ─────────────────────────────────────────

export interface CallVolatilityPart {
  /** Prévision des jours couverts, dans l'ordre. */
  forecast: readonly number[]
  /** Volatilité d'un jour, même ordre. */
  cv: readonly number[]
  cumulRatios: readonly number[] | null
}

/**
 * Volatilité d'une ligne sur l'appel, boutiques additionnées (c'est en boutique que se jouent la
 * casse et la rupture) : Σ des écarts-types cumulés de chaque boutique / Σ prévision. `null` sans
 * prévision. `plusMinus` est l'écart en UVC.
 */
export const callVolatility = (
  parts: readonly CallVolatilityPart[],
): { cv: number; plusMinus: number } | null => {
  let forecastTotal = 0
  let spreadTotal = 0
  for (const part of parts) {
    let cumulated = 0
    let spread = 0
    part.forecast.forEach((units, rank) => {
      const value = Math.max(0, units)
      const dayCv = Math.max(0, at(part.cv, rank))
      forecastTotal += value
      cumulated += dayCv * value
      if (part.cumulRatios && part.cumulRatios.length > 0) {
        const ratio = cumulRatioAt(part.cumulRatios, rank)
        spread = ratio * cumulated
      } else {
        spread += dayCv * value * Math.sqrt(1 + rank)
      }
    })
    spreadTotal += spread
  }
  return forecastTotal > 0 ? { cv: spreadTotal / forecastTotal, plusMinus: spreadTotal } : null
}

// ─── Alerte d'écart aux ventes S-1 ───────────────────────────────────────────

/**
 * Alerte d'écart avec les ventes S-1 (seuil x du Sélecteur), règle unique du back-office et de
 * l'écran unique : au-delà de x, `alerte` ; au-delà de 2x, `defavorable`. Sans ventes S-1 closes
 * (écart `null`), jamais d'alerte.
 */
export type EcartNiveau = 'alerte' | 'defavorable'

export const ecartNiveau = (ecartPct: number | null, seuil: number): EcartNiveau | null => {
  if (ecartPct === null) return null
  const ecart = Math.abs(ecartPct)
  if (ecart > 2 * seuil) return 'defavorable'
  return ecart > seuil ? 'alerte' : null
}

// ─── Jour figé d'une case ────────────────────────────────────────────────────

/**
 * La catégorie de réappro qui fait foi pour une catégorie : la principale (en tête de
 * `principales`, la part de la prévision la plus grande). `undefined` sans catégorie de réappro
 * connue.
 */
export const principalReappro = <Reappro>(
  reapproOf: (id: number) => Reappro | undefined,
  principales: readonly { id: number }[] | undefined,
): Reappro | undefined => {
  const principale = principales?.[0]
  return principale === undefined ? undefined : reapproOf(principale.id)
}

/**
 * Le jour d'une case catégorie × jour dans sa catégorie de réappro principale (`principalReappro`).
 * Règle unique du serveur (alertes de l'écran unique) et du back-office (tableau, modale) : le
 * `fige` de ce jour dit si son appel a déjà lu la prévision de la case (aucune alerte, aucune
 * correction), son appel dit qui la lira. Case par case : chaque catégorie a son appel.
 */
export const principalReapproDay = <Reappro extends { jours: readonly { date: string }[] }>(
  reapproOf: (id: number) => Reappro | undefined,
  principales: readonly { id: number }[] | undefined,
  date: string,
): { reappro: Reappro; jour: Reappro['jours'][number] | undefined } | undefined => {
  const reappro = principalReappro(reapproOf, principales)
  if (reappro === undefined) return undefined
  return { reappro, jour: reappro.jours.find((jour) => jour.date === date) }
}

// ─── Format compact (API → back-office) ──────────────────────────────────────

/**
 * Règle d'une livraison en tableau, coef effectif de la livraison en dernier : `['f', unités, coef]`
 * (commande figée), ou `['p', stock projeté, plafond, ajustement, commande arrêtée, coef]` (règle du
 * réappro, `null` = absent, arrêt 0 ou 1).
 */
export type PilotageRuleCode =
  | readonly ['f', number, number]
  | readonly ['p', number | null, number | null, number, 0 | 1, number]

/** Coef effectif porté par une règle compacte. */
export const pilotageRuleCoef = (code: PilotageRuleCode): number =>
  code[0] === 'f' ? code[2] : code[5]

/** Ce que les couples d'une référence ont en commun : son calendrier de livraison et son colis. */
export interface PilotageCompactReference {
  /** Livraisons de la fenêtre simulée ; la première est l'appel (jour 0). */
  plan: readonly { day: number; coveredDays: readonly number[] }[]
  shelfLifeDays: number
  colisage: number
}

/** Une référence × boutique en une ligne de tableaux (quelques milliers au réseau). */
export type PilotageCompactCouple = readonly [
  referenceIndex: number,
  storeIndex: number,
  /** Prévision officielle par jour depuis la livraison (UVC). */
  forecast: readonly number[],
  /** Volatilité de chaque jour (son jour de semaine), recalage compris. */
  cv: readonly number[],
  /** Indice du profil de cumul dans `cumuls`, `null` : repli en √. */
  cumulIndex: number | null,
  /** Stock par DLC : décalage en jours depuis la livraison (négatif : périme avant), unités. */
  stock: readonly (readonly [number, number])[],
  /** Demande prévue de chaque jour d'ici la livraison, la veille en dernier. */
  demandBeforeDays: readonly number[],
  /** Une règle par livraison du plan de la référence, `null` si la boutique n'est pas livrée. */
  rules: readonly (PilotageRuleCode | null)[],
  /** Multiplicateur que porte la prévision officielle de chaque jour, `null` : aucun. */
  multipliers: readonly number[] | null,
  /** Prévision du moteur par jour avant ce multiplicateur, `null` : aucun multiplicateur. */
  engineForecast: readonly number[] | null,
]

/** Règle compacte → règle de `pilotageOrderUnits`. */
export const pilotageOrderRuleOf = (code: PilotageRuleCode, colisage: number): PilotageOrderRule =>
  code[0] === 'f'
    ? { kind: 'fixed', units: code[1] }
    : {
        kind: 'forecast',
        projectedStock: code[1],
        maxUnits: code[2],
        adjustment: code[3],
        stopOrdering: code[4] === 1,
        colisage,
      }

/** Couple compact → entrée de `simulatePilotageUnit`. */
export const expandPilotageCouple = (
  couple: PilotageCompactCouple,
  reference: PilotageCompactReference,
  deliveryIso: string,
  cumuls: readonly (readonly number[])[],
): PilotageUnitInput => {
  const forecast = couple[2]
  const cumulIndex = couple[4]
  const deliveries: PilotageDelivery[] = []
  reference.plan.forEach((delivery, index) => {
    const code = couple[7][index]
    if (!code) return
    deliveries.push({
      day: delivery.day,
      coveredDays: delivery.coveredDays,
      shelfLifeDays: reference.shelfLifeDays,
      rule: pilotageOrderRuleOf(code, reference.colisage),
      coef: pilotageRuleCoef(code),
    })
  })
  return {
    deliveryIso,
    forecast,
    cv: couple[3],
    cumulRatios: cumulIndex === null ? null : (cumuls[cumulIndex] ?? null),
    stock: couple[5].map(([offset, units]) => ({ dlcIso: addDaysIso(deliveryIso, offset), units })),
    demandBeforeDays: couple[6],
    deliveries,
    multipliers: couple[8],
    engineForecast: couple[9],
  }
}
